import React, {useEffect, useRef, useState} from 'react'
import './CardInformation.scss'
import {Form, Row, Col, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useI18n} from '../../../../../Utils/_helpers/useI18n'
import ImageUploader from '../ImageUploader'
import {CardData} from '../../type'
import {CardViewType, STEP_KEYS} from '../../../../../Utils/Constant/WalletCard'
import {useWalletCard} from '../../hooks/useWalletCard'
import {useLocation} from 'react-router-dom'

interface CardInformationProps {
  cardData: CardData
  handleChange: (e: any) => void
  handleBlur: (e: any) => void
  touched: any
  errors: any
  MAX_LENGTH: number
  disableInput: boolean
  isCompleted?: boolean
  setIsCompleted?: (completed: boolean) => void
  isExpanded?: boolean
  onExpand?: (step: string) => void
  isTemplate?: boolean
  viewType?: CardViewType
}

const CardInformation: React.FC<CardInformationProps> = ({
  cardData,
  isTemplate = false,
  handleChange,
  handleBlur,
  touched,
  errors,
  MAX_LENGTH,
  disableInput,
  isCompleted,
  setIsCompleted,
  isExpanded = true,
  onExpand,
  viewType,
}) => {
  const {t} = useI18n()

  const [isContentVisible, setIsContentVisible] = useState(isExpanded)
  const [isLogoUploading, setIsLogoUploading] = useState(false)
  const [isBackgroundUploading, setIsBackgroundUploading] = useState(false)
  const [isProfileUploading, setIsProfileUploading] = useState(false)
  const [logoImage, setLogoImage] = useState<string | null>(cardData.logoGoogle || null)

  useEffect(() => {
    if (viewType === CardViewType.APPLE) {
      setLogoImage(cardData.logo || null)
    } else if (viewType === CardViewType.GOOGLE) {
      setLogoImage(cardData.logoGoogle || null)
    }
  }, [viewType, cardData.logo, cardData.logoGoogle])

  // Update content visibility when isExpanded changes
  useEffect(() => {
    setIsContentVisible(isExpanded)
  }, [isExpanded])

  // Check completion status whenever relevant fields change
  const prevIsCompleteRef = useRef<boolean>()

  useEffect(() => {
    if (setIsCompleted) {
      const requiredFields = isTemplate
        ? ['fullName', 'cardLabel', 'organizationName']
        : ['fullName', 'phone', 'email', 'cardLabel', 'organizationName']
      const isComplete = requiredFields.every((field) => {
        const value = cardData[field as keyof CardData]
        return value && String(value).trim().length > 0 && !errors[field]
      })

      // Only update if the value has changed
      if (prevIsCompleteRef.current !== isComplete) {
        setIsCompleted(isComplete)
        prevIsCompleteRef.current = isComplete
      }
    }
  }, [
    cardData.fullName,
    cardData.phone,
    cardData.email,
    cardData.cardLabel,
    cardData.organizationName,
    errors.fullName,
    errors.phone,
    errors.email,
    errors.cardLabel,
    errors.organizationName,
    setIsCompleted,
  ])

  const toggleContent = () => {
    const newState = !isContentVisible
    setIsContentVisible(newState)
    if (newState && onExpand) {
      onExpand(STEP_KEYS.CARD_DETAILS)
    }
  }

  // Handle company logo upload
  const handleCompanyLogoUpload = (appleUrl: string | null, googleUrl?: string | null) => {
    handleChange({
      target: {
        name: 'logo',
        value: appleUrl,
      },
    })

    handleChange({
      target: {
        name: 'logoGoogle',
        value: googleUrl || null,
      },
    })
  }

  // Handle profile picture upload
  const handleProfilePictureUpload = (imageUrl: string | null, googleUrl?: string | null) => {
    // Update both picture and pictureGoogle
    handleChange({
      target: {
        name: 'picture',
        value: imageUrl,
      },
    })

    handleChange({
      target: {
        name: 'pictureGoogle',
        value: googleUrl || null,
      },
    })
  }

  const handleBackgroundImageUpload = (imageUrl: string | null) => {
    handleChange({
      target: {
        name: 'backgroundImage',
        value: imageUrl,
      },
    })
  }

  const isUploading = isLogoUploading || isBackgroundUploading || isProfileUploading

  return (
    <div
      className={`wizard-step card-information ${
        isExpanded ? 'border-primary' : ''
      } border bg-white`}
    >
      <div
        className={`wizard-step-header cursor-pointer ${
          isContentVisible ? 'border-radius-bottom-0' : 'border-radius-bottom'
        }`}
        onClick={toggleContent}
      >
        <div className='step-indicator'>
          <div className={`step-number ${isCompleted ? 'completed' : 'border-secondary'} `}>
            <span></span>
            {isCompleted && (
              <svg
                className='check-icon'
                width='12'
                height='9'
                viewBox='0 0 12 9'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M10.6668 1L4.00016 7.66667L1.3335 5'
                  stroke='currentColor'
                  strokeWidth='1.67'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}
          </div>
          <div className='step-title d-flex flex-column'>
            <h2 className={`${isExpanded ? 'text-primary' : 'text-dark'} fw-semibold fs-2`}>
              {t('WALLET_CARD.CARD_INFORMATION.TITLE')}
            </h2>
            <span>{t('WALLET_CARD.CARD_INFORMATION.SUBTITLE')}</span>
          </div>
          <h2
            className={`${
              isExpanded ? 'text-primary' : 'text-muted'
            } fw-semibold fs-2 text-number-step`}
          >
            2
          </h2>
          <div className={`collapse-arrow ${!isContentVisible ? 'collapsed' : ''}`}>
            <svg
              width='10'
              height='6'
              viewBox='0 0 10 6'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M1 1L5 5L9 1'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>
      </div>

      <div className={`wizard-step-content ${!isContentVisible ? 'd-none' : 'p-4'}`}>
        <div className='card-information-form'>
          {/* Upload Buttons */}
          <Row className='mb-5'>
            <Col md={4} className={`${isTemplate ? '' : 'd-none'}`}>
              <ImageUploader
                label={t('WALLET_CARD.FORM.COMPANY_LOGO')}
                type='logo'
                currentImage={logoImage}
                onImageSelected={handleCompanyLogoUpload}
                disabled={disableInput || isUploading || !isTemplate}
                onUploadStart={() => setIsLogoUploading(true)}
                onUploadEnd={() => setIsLogoUploading(false)}
                cropWidth={300}
                cropHeight={150}
                aspectRatio={1}
                isTemplate={isTemplate}
                tooltip={t('MOBILE_WALLET_CARD.FORM.COMPANY_LOGO_TOOLTIP')}
                showBothCropper={true}
                allowFreeAspect={true}
                cardData={cardData}
              />
            </Col>
            <Col md={4} className={`${isTemplate ? '' : 'd-none'}`}>
              <ImageUploader
                label={t('WALLET_CARD.FORM.BACKGROUND_CONTENT')}
                type='background'
                currentImage={cardData.backgroundImage}
                onImageSelected={handleBackgroundImageUpload}
                disabled={disableInput || isUploading || !isTemplate}
                onUploadStart={() => setIsBackgroundUploading(true)}
                onUploadEnd={() => setIsBackgroundUploading(false)}
                cropWidth={360}
                cropHeight={180}
                aspectRatio={16 / 9}
                isTemplate={isTemplate}
                tooltip={t('MOBILE_WALLET_CARD.FORM.BACKGROUND_CONTENT_TOOLTIP')}
              />
            </Col>
            <Col md={4} className={`${isTemplate ? 'd-none' : ''}`}>
              <ImageUploader
                label={t('WALLET_CARD.FORM.PROFILE_PICTURE')}
                type='profile'
                shape='circle'
                currentImage={cardData.pictureGoogle}
                onImageSelected={(imageUrl, googleUrl) =>
                  handleProfilePictureUpload(imageUrl, googleUrl)
                }
                disabled={disableInput || isUploading || isTemplate}
                onUploadStart={() => setIsProfileUploading(true)}
                onUploadEnd={() => setIsProfileUploading(false)}
                cropWidth={320}
                cropHeight={320}
                uploadWidth={1032}
                uploadHeight={320}
              />
            </Col>
          </Row>

          <Form.Group className='mb-3'>
            <Form.Label className='d-flex'>
              {t('WALLET_CARD.FORM.FULL_NAME')}
              {isTemplate && (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id='tooltip'>{t('MOBILE_WALLET_CARD.FORM.FULL_NAME_TOOLTIP')}</Tooltip>
                  }
                >
                  <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer ms-1 mt-1'></i>
                </OverlayTrigger>
              )}
              <span className='text-danger ms-1'>*</span>
            </Form.Label>
            <Form.Control
              type='text'
              name='fullName'
              value={cardData.fullName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t('WALLET_CARD.PLACEHOLDER.FULL_NAME')}
              required
              isInvalid={touched.fullName && !!errors.fullName}
              maxLength={MAX_LENGTH}
              disabled={disableInput || isTemplate}
              className='input-card-information'
            />
            {touched.fullName && errors.fullName && (
              <div className='fv-plugins-message-container'>
                <span role='alert' className='text-danger'>
                  {errors.fullName}
                </span>
              </div>
            )}
          </Form.Group>

          {/* Card Label */}

          <Form.Group className={`mb-4 ${isTemplate ? '' : 'd-none'}`}>
            <Form.Label className='form-label d-flex gap-1'>
              {t('WALLET_CARD.FORM.CARD_LABEL')}
              {isTemplate && (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id='tooltip'>
                      {t('MOBILE_WALLET_CARD.FORM.CARD_LABEL_TOOLTIP')}
                    </Tooltip>
                  }
                >
                  <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer ms-1 mt-1'></i>
                </OverlayTrigger>
              )}
              <span className='text-danger ms-1'>*</span>
            </Form.Label>
            <Form.Control
              type='text'
              name='cardLabel'
              value={cardData.cardLabel}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              isInvalid={touched.cardLabel && !!errors.cardLabel}
              placeholder={t('WALLET_CARD.FORM.CARD_LABEL_PLACEHOLDER')}
              maxLength={MAX_LENGTH}
              disabled={disableInput || !isTemplate}
              className='input-card-information'
            />
            {touched.cardLabel && errors.cardLabel && (
              <div className='fv-plugins-message-container'>
                <span role='alert' className='text-danger'>
                  {errors.cardLabel}
                </span>
              </div>
            )}
          </Form.Group>

          {/* Organization Name */}

          <Form.Group className={`mb-4 ${isTemplate ? '' : 'd-none'}`}>
            <Form.Label className='form-label d-flex gap-1'>
              {t('WALLET_CARD.FORM.ORGANIZATION_NAME')}
              {isTemplate && (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id='tooltip'>
                      {t('MOBILE_WALLET_CARD.FORM.ORGANIZATION_NAME_TOOLTIP')}
                    </Tooltip>
                  }
                >
                  <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer ms-1 mt-1'></i>
                </OverlayTrigger>
              )}
              <span className='text-danger ms-1'>*</span>
            </Form.Label>
            <Form.Control
              type='text'
              name='organizationName'
              value={cardData.organizationName}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              isInvalid={touched.organizationName && !!errors.organizationName}
              placeholder={t('WALLET_CARD.PLACEHOLDER.ORGANIZATION_NAME')}
              maxLength={MAX_LENGTH}
              disabled={disableInput || !isTemplate}
              className='input-card-information'
            />
            {touched.organizationName && errors.organizationName && (
              <div className='fv-plugins-message-container'>
                <span role='alert' className='text-danger'>
                  {errors.organizationName}
                </span>
              </div>
            )}
          </Form.Group>

          {/* Job Title */}

          <Form.Group className='mb-3'>
            <Form.Label className='d-flex'>
              {t('WALLET_CARD.FORM.JOB_TITLE')}
              {isTemplate && (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id='tooltip'>{t('MOBILE_WALLET_CARD.FORM.JOB_TITLE_TOOLTIP')}</Tooltip>
                  }
                >
                  <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer ms-1 mt-1'></i>
                </OverlayTrigger>
              )}
            </Form.Label>
            <Form.Control
              type='text'
              name='jobTitle'
              value={cardData.jobTitle}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t('WALLET_CARD.PLACEHOLDER.JOB_TITLE')}
              maxLength={MAX_LENGTH}
              disabled={disableInput || isTemplate}
              className='input-card-information'
            />
          </Form.Group>

          <Form.Group className={`mb-3 ${isTemplate ? '' : 'd-none'}`}>
            <Form.Label className='d-flex'>
              {t('WALLET_CARD.FORM.COMPANY_NAME')}
              {isTemplate && (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id='tooltip'>
                      {t('MOBILE_WALLET_CARD.FORM.COMPANY_NAME_TOOLTIP')}
                    </Tooltip>
                  }
                >
                  <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer ms-1 mt-1'></i>
                </OverlayTrigger>
              )}
            </Form.Label>
            <Form.Control
              type='text'
              name='companyName'
              value={cardData.companyName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t('WALLET_CARD.PLACEHOLDER.COMPANY_NAME')}
              maxLength={MAX_LENGTH}
              disabled={disableInput || !isTemplate}
              className='input-card-information'
            />
          </Form.Group>

          <Form.Group className={`mb-3 ${isTemplate ? '' : 'd-none'}`}>
            <Form.Label className='d-flex'>
              {t('WALLET_CARD.FORM.ADDRESS')}
              {isTemplate && (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id='tooltip'>{t('MOBILE_WALLET_CARD.FORM.ADDRESS_TOOLTIP')}</Tooltip>
                  }
                >
                  <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer ms-1 mt-1'></i>
                </OverlayTrigger>
              )}
            </Form.Label>
            <Form.Control
              type='text'
              name='address'
              value={cardData.address}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t('WALLET_CARD.PLACEHOLDER.ADDRESS')}
              maxLength={MAX_LENGTH}
              disabled={disableInput || !isTemplate}
              className='input-card-information'
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label className='d-flex'>
              {t('WALLET_CARD.FORM.PHONE')}
              {isTemplate && (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id='tooltip'>{t('MOBILE_WALLET_CARD.FORM.PHONE_TOOLTIP')}</Tooltip>
                  }
                >
                  <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer ms-1 mt-1'></i>
                </OverlayTrigger>
              )}
              {!isTemplate && <span className='text-danger'>*</span>}
            </Form.Label>
            <Form.Control
              type='text'
              name='phone'
              value={cardData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t('WALLET_CARD.PLACEHOLDER.PHONE')}
              required={isTemplate ? false : true}
              isInvalid={touched.phone && !!errors.phone}
              disabled={disableInput}
              className='input-card-information'
            />
            {touched.phone && errors.phone && (
              <div className='fv-plugins-message-container'>
                <span role='alert' className='text-danger'>
                  {errors.phone}
                </span>
              </div>
            )}
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label className='d-flex'>
              {isTemplate
                ? t('MOBILE_WALLET_CARD.FORM.TEMPLATE.EMAIL')
                : t('WALLET_CARD.FORM.EMAIL')}
              {isTemplate && (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id='tooltip'>{t('MOBILE_WALLET_CARD.FORM.EMAIL_TOOLTIP')}</Tooltip>
                  }
                >
                  <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer ms-1 mt-1'></i>
                </OverlayTrigger>
              )}
              {!isTemplate && <span className='text-danger ms-1'>*</span>}
            </Form.Label>
            <Form.Control
              type='email'
              name='email'
              value={cardData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t('WALLET_CARD.PLACEHOLDER.EMAIL')}
              required={!isTemplate}
              isInvalid={touched.email && !!errors.email}
              maxLength={MAX_LENGTH}
              disabled={disableInput}
              className='input-card-information'
            />
            {touched.email && errors.email && (
              <div className='fv-plugins-message-container'>
                <span role='alert' className='text-danger'>
                  {errors.email}
                </span>
              </div>
            )}
          </Form.Group>

          <Form.Group className={`mb-4 ${isTemplate ? '' : 'd-none'}`}>
            <Form.Label className='d-flex'>
              {t('WALLET_CARD.FORM.WEBSITE')}
              {isTemplate && (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id='tooltip'>{t('MOBILE_WALLET_CARD.FORM.WEBSITE_TOOLTIP')}</Tooltip>
                  }
                >
                  <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer ms-1 mt-1'></i>
                </OverlayTrigger>
              )}
            </Form.Label>
            <Form.Control
              type='text'
              name='website'
              value={cardData.website}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t('WALLET_CARD.PLACEHOLDER.WEBSITE')}
              maxLength={MAX_LENGTH}
              isInvalid={touched.website && !!errors.website}
              disabled={disableInput || !isTemplate}
              className='input-card-information'
            />
            {touched.website && errors.website && (
              <div className='fv-plugins-message-container'>
                <span role='alert' className='text-danger'>
                  {errors.website}
                </span>
              </div>
            )}
          </Form.Group>
          {/* Color Picker */}
          {isTemplate && (
            <Form.Group className='mb-8'>
              <Row className='gap-2'>
                <Col md={5}>
                  <Form.Label>{t('WALLET_CARD.FORM.BACKGROUND_COLOR')}</Form.Label>
                  <div className='color-picker-group'>
                    <div className='color-preview-box-wrapper'>
                      <div
                        className='color-preview-box'
                        style={{background: cardData.backgroundColor}}
                      />
                      <input
                        id='colorPickerInput'
                        type='color'
                        name='backgroundColor'
                        value={cardData.backgroundColor}
                        onChange={handleChange}
                        className='color-picker-input'
                        tabIndex={-1}
                        aria-label='Choose color'
                      />
                    </div>
                    <input
                      type='text'
                      name='backgroundColor'
                      value={cardData.backgroundColor}
                      onChange={handleChange}
                      maxLength={30}
                      className='color-hex-input input-card-information'
                      disabled={disableInput}
                    />
                  </div>
                </Col>
                <Col md={5}>
                  <Form.Label>{t('WALLET_CARD.FORM.LABEL_COLOR')}</Form.Label>
                  <div className='color-picker-group'>
                    <div className='color-preview-box-wrapper'>
                      <div
                        className='color-preview-box'
                        style={{background: cardData.labelColor}}
                      />
                      <input
                        id='labelColorPickerInput'
                        type='color'
                        name='labelColor'
                        value={
                          /^#(?:fff|ffffff|[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(
                            cardData.labelColor || ''
                          )
                            ? cardData.labelColor
                            : '#000000'
                        }
                        onChange={handleChange}
                        disabled={disableInput}
                        className='color-picker-input'
                        tabIndex={-1}
                        aria-label='Choose text color'
                      />
                    </div>
                    <input
                      type='text'
                      name='labelColor'
                      value={cardData.labelColor}
                      onChange={handleChange}
                      maxLength={30}
                      className='color-hex-input input-card-information'
                      disabled={disableInput}
                    />
                  </div>
                </Col>
                <Col md={5}>
                  <Form.Label>{t('WALLET_CARD.FORM.FOREGROUND_COLOR')}</Form.Label>
                  <div className='color-picker-group'>
                    <div className='color-preview-box-wrapper'>
                      <div
                        className='color-preview-box'
                        style={{background: cardData.foregroundColor}}
                      />
                      <input
                        id='textColorPickerInput'
                        type='color'
                        name='foregroundColor'
                        value={
                          /^#(?:fff|ffffff|[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(
                            cardData.foregroundColor || ''
                          )
                            ? cardData.foregroundColor
                            : '#000000'
                        }
                        onChange={handleChange}
                        disabled={disableInput}
                        className='color-picker-input'
                        tabIndex={-1}
                        aria-label='Choose text color'
                      />
                    </div>
                    <input
                      type='text'
                      name='foregroundColor'
                      value={cardData.foregroundColor}
                      onChange={handleChange}
                      maxLength={30}
                      className='color-hex-input input-card-information'
                      disabled={disableInput}
                    />
                  </div>
                </Col>
                <Col md={5}>
                  <Form.Label>{t('WALLET_CARD.FORM.ICON_COLOR')}</Form.Label>
                  <div className='color-picker-group'>
                    <div className='color-preview-box-wrapper'>
                      <div
                        className='color-preview-box'
                        style={{background: cardData.contactIconColor}}
                      />
                      <input
                        id='iconColorPickerInput'
                        type='color'
                        name='contactIconColor'
                        value={cardData.contactIconColor || '#8734cb'}
                        onChange={handleChange}
                        disabled={disableInput}
                        className='color-picker-input'
                        tabIndex={-1}
                        aria-label='Choose icon color'
                      />
                    </div>
                    <input
                      type='text'
                      name='contactIconColor'
                      value={cardData.contactIconColor}
                      onChange={handleChange}
                      maxLength={30}
                      className='color-hex-input input-card-information'
                      disabled={disableInput}
                    />
                  </div>
                </Col>
              </Row>
            </Form.Group>
          )}
        </div>
      </div>
    </div>
  )
}

export default CardInformation
